// 広告配信基盤（Hagumi Ad Platform）との連携。
//
// 選定は backend の /api/ad/select が担当する。
// 候補一覧には商材メモや CTR が含まれ、ブラウザに出したくない情報なので、
// Supabase の get_creative_candidates はサーバーサイド（service_role）からしか呼べない。
//
// 計測ログ（表示・クリック）はブラウザから Supabase の RPC を直接叩く。
//   record_impression … 実際に画面へ出た時点で記録する
//   record_click      … クリックを記録し、遷移先URLを受け取る
//
// 広告は「あれば出す」もの。取得に失敗しても会話体験を止めないため、
// このモジュールは例外を投げず、常に null / 安全な値を返す。
//
// ファイル名・クラス名に "ad" / "ads" を使っていないのは意図的。
// EasyList には `##.ad-slot` のような全ドメイン対象の非表示ルールがあり、
// 広告ブロッカーを入れたブラウザでは localhost でも問答無用で消される。
// 名前を変えるときは、フィルタリストに載っていない語を選ぶこと。
import { createClient } from '@supabase/supabase-js';
// バックエンドの場所は api.js と共通（REACT_APP_API_BASE で切り替え）
import { API_BASE } from './api';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// このサービスの識別子（CMS の services.slug と一致させる）
export const SERVICE_SLUG = 'bokuwaha';

// CMS の placements.slug と一致させる
export const PLACEMENTS = {
  table: 'table_product',
  banner: 'banner_bottom',
};

export const promoEnabled = Boolean(SUPABASE_URL && SUPABASE_KEY);

const supabase = promoEnabled
  ? createClient(SUPABASE_URL, SUPABASE_KEY, {
      // 広告の閲覧にログインは不要。セッションを保存しない
      auth: { persistSession: false, autoRefreshToken: false },
    })
  : null;

if (!promoEnabled) {
  console.warn(
    '[promo] REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY が読み込まれていません。' +
      ' .env を追加・変更したあとは dev サーバーの再起動が必要です。',
  );
}

// 表示とクリックを同じセッションとして紐付けるためのID。
// タブを閉じるまで有効（sessionStorage）。
const SESSION_KEY = 'bokubana_promo_session_id';

function getSessionId() {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // プライベートブラウズなどで sessionStorage が使えない場合
    return null;
  }
}

/**
 * 会話の流れ・お題・過去の実績をもとに、AIが選んだ広告を1件受け取る。
 * 配信対象がなければ null（＝枠に何も出さない）。
 *
 * @param {string} placementSlug PLACEMENTS のいずれか
 * @param {string|null} keyword  お題
 * @param {{speaker:string,text:string}[]} conversation これまでの会話
 * @param {string[]} excludeCreativeIds 出したくないクリエイティブ
 */
export async function fetchPromo(
  placementSlug,
  keyword,
  conversation = [],
  excludeCreativeIds = [],
) {
  try {
    const response = await fetch(`${API_BASE}/api/ad/select`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_slug: SERVICE_SLUG,
        placement_slug: placementSlug,
        keyword: keyword || null,
        // セリフだけを渡す。表示には使わないので id などは落とす
        conversation: conversation.map((l) => ({
          speaker: l.speaker,
          text: l.text,
        })),
        exclude_creative_ids: excludeCreativeIds,
        // 選定ログと表示ログを突き合わせるために渡す
        session_id: getSessionId(),
      }),
    });

    if (!response.ok) {
      console.warn('[promo] 選定APIが', response.status, 'を返しました');
      return null;
    }

    const data = await response.json();
    // アセットが無いものは表示しようがないので出さない
    if (!data || !Array.isArray(data.assets) || data.assets.length === 0) return null;
    return data;
  } catch (e) {
    console.warn('[promo] 選定APIの呼び出しに失敗:', e);
    return null;
  }
}

/**
 * 表示ログを記録する。実際に画面へ出た時点で呼ぶこと。
 * 選定時ではなく表示時に記録するのは、選ばれたが表示されなかった分を
 * 分母に入れないため（AIがCTRを判断材料に使うので精度が要る）。
 */
export async function reportImpression(creativeId, placementSlug, keyword) {
  if (!supabase) return;

  try {
    const { error } = await supabase.rpc('record_impression', {
      p_creative_id: creativeId,
      p_service_slug: SERVICE_SLUG,
      p_placement_slug: placementSlug,
      p_session_id: getSessionId(),
      p_keyword: keyword || null,
    });
    if (error) console.warn('[promo] record_impression failed:', error.message);
  } catch (e) {
    console.warn('[promo] record_impression error:', e);
  }
}

/**
 * クリックを記録して遷移先URLを返す。失敗時は null。
 * クリエイティブは複数の枠に紐付きうるので、どの枠でのクリックかを必ず渡す。
 */
export async function reportClick(creativeId, placementSlug, keyword) {
  if (!supabase) return null;

  try {
    const { data, error } = await supabase.rpc('record_click', {
      p_creative_id: creativeId,
      p_service_slug: SERVICE_SLUG,
      p_placement_slug: placementSlug,
      p_session_id: getSessionId(),
      p_keyword: keyword || null,
    });

    if (error) {
      console.warn('[promo] record_click failed:', error.message);
      return null;
    }
    return data || null;
  } catch (e) {
    console.warn('[promo] record_click error:', e);
    return null;
  }
}
